import { buildApiUrl, API_CONFIG } from '../config';
import { apiFetch } from '../utils/auth';

// Team API functions
export const teamAPI = {
  // Get all teams for an operation
  // Bei technischen Accounts kann eine supervisorID übergeben werden,
  // damit die API nach der echten Person filtert statt nach dem JWT-User
  getTeams: async (opsID: number, supervisorID?: number) => {
    let url = buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS(opsID));
    if (supervisorID) {
      url += `?supervisorID=${supervisorID}`;
      console.log('👤 getTeams: Filtere nach supervisorID (echte Person):', supervisorID);
    }
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Get all teams for an operation (by operation ID)
  getTeamsByOperation: async (orgID: number, opsID: number) => {
    console.log('🌐 API CALL: POST /api/v1/teams/by-operation', { orgID, opsID });
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_BY_OPERATION), {
      method: 'POST',
      body: JSON.stringify({ orgID, opsID }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Create new team
  createTeam: async (teamData: {
    opsID: number;
    name: string;
    task?: string;
    location?: string;
    startPressure?: number;
    radioChannel?: string;
    teamLeaderIndex?: number;
    creatorID: number;
    members?: Array<{
      firstName: string;
      lastName: string;
      pressure?: number;
    }>;
  }) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_CREATE), {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Update team
  updateTeam: async (teamId: number, teamData: any) => {
    console.log('🌐 API CALL: PUT /api/v1/teams/' + teamId, teamData);
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_UPDATE(teamId)), {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Delete team
  deleteTeam: async (teamId: number) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_DELETE(teamId)), {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Get team members
  getTeamMembers: async (teamId: number) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_MEMBERS(teamId)));
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Add member to team
  addTeamMember: async (teamId: number, memberData: {
    firstName: string;
    lastName: string;
    pressure?: number;
  }) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_MEMBERS_ADD(teamId)), {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Remove member from team
  removeTeamMember: async (teamId: number, memberId: number) => {
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.TEAMS_MEMBERS_DELETE(teamId, memberId)), {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
};

// Settings API functions
export const settingsAPI = {
  // Normalize backend values to strict boolean
  normalizeQuickTruppEnabled: (value: any): boolean => {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value === null || value === undefined) return false;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1') return true;
      if (normalized === 'false' || normalized === '0' || normalized === '') return false;
    }
    return Boolean(value);
  },

  // Extract quick-trupp value from different backend response shapes
  extractQuickTruppEnabled: (payload: any): boolean => {
    const candidates = [
      payload?.enabled,
      payload?.quickTruppCreation,
      payload?.quick_trupp_creation,
      payload?.value,
      payload?.settingValue,
      payload?.data?.enabled,
      payload?.data?.quickTruppCreation,
      payload?.data?.quick_trupp_creation,
      payload?.data?.value,
      Array.isArray(payload) ? payload[0]?.enabled : undefined,
      Array.isArray(payload) ? payload[0]?.quickTruppCreation : undefined,
      Array.isArray(payload) ? payload[0]?.quick_trupp_creation : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.enabled : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.quickTruppCreation : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.quick_trupp_creation : undefined,
    ];

    for (const candidate of candidates) {
      if (candidate !== undefined) {
        return settingsAPI.normalizeQuickTruppEnabled(candidate);
      }
    }

    return false;
  },

  // Get quick trupp creation setting
  getQuickTruppCreation: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/quick-trupp-creation?orgID=${orgID}&_ts=${cacheBuster}`);
    console.log('🌐 API CALL: GET /api/v1/settings/quick-trupp-creation', { orgID, cacheBuster, url });
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const enabled = settingsAPI.extractQuickTruppEnabled(data);
    console.log('✅ API RESPONSE: GET /api/v1/settings/quick-trupp-creation', {
      orgID,
      raw: data,
      extractedEnabled: enabled,
    });
    return {
      ...data,
      enabled,
    };
  },
  
  // Set quick trupp creation setting
  setQuickTruppCreation: async (orgID: number, enabled: boolean) => {
    console.log('🌐 API CALL: POST /api/v1/settings/quick-trupp-creation', { enabled, orgID });
    const response = await apiFetch(buildApiUrl('/api/v1/settings/quick-trupp-creation'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orgID, enabled }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    console.log('✅ API RESPONSE: POST /api/v1/settings/quick-trupp-creation', {
      orgID,
      requestedEnabled: enabled,
      raw: data,
    });
    return data;
  },

  normalizeTruppNamingMode: (value: any): 'leader' | 'number' => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'number') return 'number';
      if (normalized === 'leader') return 'leader';
    }
    return 'leader';
  },

  extractTruppNamingMode: (payload: any): 'leader' | 'number' => {
    const candidates = [
      payload?.mode,
      payload?.truppNamingMode,
      payload?.trupp_naming_mode,
      payload?.value,
      payload?.settingValue,
      payload?.data?.mode,
      payload?.data?.truppNamingMode,
      payload?.data?.trupp_naming_mode,
      payload?.data?.value,
      Array.isArray(payload) ? payload[0]?.mode : undefined,
      Array.isArray(payload) ? payload[0]?.value : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.mode : undefined,
      Array.isArray(payload?.data) ? payload.data[0]?.value : undefined,
    ];

    for (const candidate of candidates) {
      if (candidate !== undefined && candidate !== null) {
        return settingsAPI.normalizeTruppNamingMode(candidate);
      }
    }

    return 'leader';
  },

  getTruppNamingMode: async (orgID: number) => {
    const cacheBuster = Date.now();
    const url = buildApiUrl(`/api/v1/settings/trupp-naming-mode?orgID=${orgID}&_ts=${cacheBuster}`);
    console.log('🌐 API CALL: GET /api/v1/settings/trupp-naming-mode', { orgID, cacheBuster, url });
    const response = await apiFetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const mode = settingsAPI.extractTruppNamingMode(data);
    console.log('✅ API RESPONSE: GET /api/v1/settings/trupp-naming-mode', {
      orgID,
      raw: data,
      extractedMode: mode,
    });
    return {
      ...data,
      mode,
    };
  },

  setTruppNamingMode: async (orgID: number, mode: 'leader' | 'number') => {
    console.log('🌐 API CALL: POST /api/v1/settings/trupp-naming-mode', { orgID, mode });
    const response = await apiFetch(buildApiUrl('/api/v1/settings/trupp-naming-mode'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orgID, mode }),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    console.log('✅ API RESPONSE: POST /api/v1/settings/trupp-naming-mode', {
      orgID,
      requestedMode: mode,
      raw: data,
    });
    return data;
  },
};

// Audit Log API functions
export const auditLogAPI = {
  // Get audit log for an operation
  getAuditLog: async (opsID: number) => {
    console.log('🌐 API CALL: GET /api/v1/auditlog/' + opsID);
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.AUDITLOG(opsID)));
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Get audit log for a specific team
  getAuditLogForTeam: async (opsID: number, teamID: number) => {
    console.log('🌐 API CALL: GET /api/v1/auditlog/' + opsID + '/team/' + teamID);
    const response = await apiFetch(buildApiUrl(`/api/v1/auditlog/${opsID}/team/${teamID}`));
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
  
  // Create audit log entry
  createAuditLog: async (logData: {
    opsID: number;
    teamID?: number;
    logType: string;
    message: string;
    shortDescription?: string;
    logData?: any;
  }) => {
    console.log('🌐 API CALL: POST /api/v1/auditlog', logData);
    const response = await apiFetch(buildApiUrl(API_CONFIG.ENDPOINTS.AUDITLOG_CREATE), {
      method: 'POST',
      body: JSON.stringify(logData),
    });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    return response.json();
  },
};

export default {
  team: teamAPI,
  settings: settingsAPI,
  auditLog: auditLogAPI,
}; 